import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Repeat, Plus, PlayCircle, PauseCircle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { GBP } from '../invoices/constants';

const FREQ_LABEL = { monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually' };
const STATUS_TONE = {
    active:    'bg-emerald-50 text-emerald-700 border-emerald-200',
    paused:    'bg-amber-50 text-amber-800 border-amber-200',
    completed: 'bg-slate-100 text-slate-600 border-slate-200',
};

function scheduleTotal(s) {
    const rate = Number(s.vat_rate ?? 20);
    let sub = 0, vat = 0;
    for (const li of (s.line_items || [])) {
        const line = Math.round((Number(li.qty) || 0) * (Number(li.unit_price) || 0) * 100) / 100;
        sub += line;
        if (li.vat_applies !== false) vat += line * rate / 100;
    }
    return Math.round((sub + vat) * 100) / 100;
}

export default function RecurringList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/recurring-invoices');
            setItems(r.data.items);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const runDue = async () => {
        setRunning(true);
        try {
            const r = await api.post('/recurring-invoices/run-now');
            toast.success(r.data.generated > 0
                ? `Generated ${r.data.generated} invoice(s) from due schedules`
                : 'No schedules are due right now');
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
        finally { setRunning(false); }
    };

    const toggleStatus = async (s, e) => {
        e.stopPropagation();
        const next = s.status === 'active' ? 'paused' : 'active';
        try {
            await api.patch(`/recurring-invoices/${s.id}`, { status: next });
            toast.success(next === 'active' ? 'Schedule resumed' : 'Schedule paused');
            load();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || 'Failed'); }
    };

    return (
        <div data-testid="recurring-list-page" className="bm-fade-in">
            <PageHeader
                title="Recurring invoices"
                description="Set up once — invoices auto-generate on schedule, with payment links, and optionally auto-send to the customer."
                actions={
                    <div className="flex gap-2">
                        <Button onClick={runDue} disabled={running} variant="outline" className="h-9" data-testid="recurring-run-now-btn">
                            <Zap size={15} className="mr-1.5" /> {running ? 'Running…' : 'Run due now'}
                        </Button>
                        <Button onClick={() => navigate('/recurring/new')} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="recurring-new-btn">
                            <Plus size={16} className="mr-1" /> New schedule
                        </Button>
                    </div>
                }
            />

            <div className="px-8 py-6">
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                            <tr>
                                <th className="text-left px-5 py-3 font-medium">Schedule</th>
                                <th className="text-left px-4 py-3 font-medium">Client</th>
                                <th className="text-left px-4 py-3 font-medium">Frequency</th>
                                <th className="text-left px-4 py-3 font-medium">Next invoice</th>
                                <th className="text-right px-4 py-3 font-medium">Amount</th>
                                <th className="text-center px-4 py-3 font-medium">Auto-send</th>
                                <th className="text-left px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={8} className="px-5 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={8} className="px-5 py-14 text-center">
                                    <Repeat size={36} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                                    <div className="mt-3 text-[color:var(--bm-text-muted)] text-[14px]">No recurring schedules yet.</div>
                                    <div className="text-[12.5px] text-[color:var(--bm-text-subtle)] mt-1">Create one to auto-bill a client every month, quarter or year.</div>
                                </td></tr>
                            )}
                            {items.map(s => (
                                <tr key={s.id} onClick={() => navigate(`/recurring/${s.id}`)}
                                    className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer"
                                    data-testid={`recurring-row-${s.id}`}>
                                    <td className="px-5 py-3.5 font-medium text-[color:var(--bm-text)]">{s.title}</td>
                                    <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{s.client_name}</td>
                                    <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{FREQ_LABEL[s.frequency] || s.frequency}</td>
                                    <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{s.status === 'completed' ? '—' : s.next_run_date}</td>
                                    <td className="px-4 py-3.5 text-right font-medium">{GBP(scheduleTotal(s))}</td>
                                    <td className="px-4 py-3.5 text-center">
                                        {s.auto_send
                                            ? <span className="bm-pill bg-emerald-50 text-emerald-700 border border-emerald-200">On</span>
                                            : <span className="bm-pill bg-slate-100 text-slate-500 border border-slate-200">Draft</span>}
                                    </td>
                                    <td className="px-4 py-3.5">
                                        <span className={`bm-pill border ${STATUS_TONE[s.status] || ''}`}>{s.status}</span>
                                    </td>
                                    <td className="px-4 py-3.5 text-right">
                                        {s.status !== 'completed' && (
                                            <button onClick={(e) => toggleStatus(s, e)} className="text-[color:var(--bm-text-subtle)] hover:text-[color:var(--bm-primary)] p-1"
                                                    title={s.status === 'active' ? 'Pause' : 'Resume'} data-testid={`recurring-toggle-${s.id}`}>
                                                {s.status === 'active' ? <PauseCircle size={17} /> : <PlayCircle size={17} />}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
